import axios from 'axios';
import tokenUtils from '../utils/tokenUtils';
import Notification from '../utils/notification';

// Environment configuration
const getApiBaseUrl = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  // Environment-specific API URLs
  const apiUrls = {
    development: '/pan-platform/api', // Use relative URL for proxy with context path
    uat: 'https://api-uat.yourcompany.com/pan-platform/api',
    production: 'https://api.yourcompany.com/pan-platform/api'
  };

  return process.env.REACT_APP_API_URL || apiUrls[env] || apiUrls.development;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

api.interceptors.request.use(
  (config) => {
    const token = tokenUtils.getToken();

    // Check if token is expired before making request
    if (token && tokenUtils.isTokenExpired(token)) {
      console.log('Token expired, clearing auth data');
      tokenUtils.clearAuth();
      tokenUtils.redirectToLogin();
      return Promise.reject(new Error('Token expired'));
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Check if response body contains error code
    if (response.data && response.data.code && response.data.code !== 200) {
      const errorMessage = response.data.message || '请求失败，请稍后重试';
      Notification.error(errorMessage);

      // Reject the promise to trigger catch blocks
      return Promise.reject({
        response: response,
        message: errorMessage
      });
    }
    return response;
  },
  (error) => {
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      console.log('Received 401 response - token expired or invalid');
      tokenUtils.clearAuth();
      tokenUtils.redirectToLogin();
      return Promise.reject(error);
    }

    // Extract and show error message for all other errors
    let errorMessage = '请求失败，请稍后重试';

    if (error.response?.data) {
      // Try different error message formats
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.msg) {
        errorMessage = error.response.data.msg;
      } else if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Show error notification
    Notification.error(errorMessage);

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/v1/auth/login', { usernameOrEmail: email, password }),
  register: (email, password, confirmPassword, inviteCode) => api.post('/v1/auth/register', {
    email,
    password,
    confirmPassword,
    inviteCode
  }),
};

export const searchAPI = {
  search: (query, page = 1, limit = 20, sortBy = 'created_at', sortOrder = 'desc', tags = null, diskSource = null) =>
    api.post('/v1/search/resources', {
      keyword: query,
      tags: tags,
      source: diskSource,
      page,
      size: limit,
      sortBy,
      sortOrder
    }),

  searchSuggestions: (query) =>
    api.get('/v1/search/suggestions', { params: { keyword: query, limit: 10 } }),

  getResource: (id) => api.get(`/v1/search/resources/${id}`),

  getLatestResources: (limit = 20) => api.get('/v1/search/resources/latest', { params: { limit } }),

  getDiskSources: () => api.get('/v1/search/resources/sources'),

  getPopularKeywords: (limit = 10) => api.get('/v1/search/popular', { params: { limit } }),
};

export const userAPI = {
  checkin: () => api.post('/v1/checkin'),
  getProfile: () => api.get('/v1/user/profile'),
  getPointsHistory: (params = {}) => {
    const { page = 1, limit = 20, type = 'all', startDate, endDate } = params;
    const queryParams = { page, limit, type };
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;
    return api.get('/v1/points/history', { params: queryParams });
  },
  getInvitedUsers: () => api.get('/v1/invitations/users'),
  purchaseResource: (resourceId) => api.post('/v1/resources/purchase', { resourceId }),
  getDownloadLink: (resourceId) => api.get(`/v1/resources/${resourceId}/download`),
  changePassword: (data) => api.post('/v1/user/change-password', data),
  getCheckinStats: () => api.get('/v1/checkin/stats'),
  getMonthlyCheckins: (year = 0, month = 0) => api.get('/v1/checkin/monthly', { params: { year, month } }),
};

export const tagsAPI = {
  getAllTags: (page = 1, size = 20) => api.get('/v1/tags', { params: { page, size } }),
};

export default api;